import { NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { Modal, Field, inputClass, PrimaryButton } from "./common";
import { CameraCapture } from "./CameraCapture";
import { ChatWidget } from "./ChatWidget";
import { GuidedTour } from "./GuidedTour";
import { GlobalSearch } from "./GlobalSearch";
import { NotificationsBell } from "./NotificationsBell";
import { toast } from "sonner";
import {
  SquaresFour, Users, FileText, Receipt, Camera, IdentificationCard,
  ShieldCheck, SignOut, List, X, HardHat, Fingerprint, Stack, Bank, Blueprint, Coins, UserGear, Notebook, ClockCounterClockwise, ShareNetwork, Scales, FilePdf, WhatsappLogo, ChatCircleDots, ListChecks, ChartBar, Table, Key, Trash, ClipboardText, MapTrifold, Storefront, Cube, ArrowSquareOut, Compass, AddressBook,
} from "@phosphor-icons/react";

const nav = [
  { href: "https://app.vendorlegal.mx/validaciones", label: "VENDOR", icon: ShareNetwork, external: true },
  { href: "https://plazatarantella.operam.pro/", label: "OPERAM (ERP)", icon: Bank, external: true },
  { to: "/", label: "Panel", icon: SquaresFour, exact: true, contractor: true },
  { to: "/contratistas", label: "Contratistas", icon: Users },
  { to: "/proveedores", label: "Proveedores", icon: Storefront },
  { to: "/contratos", label: "Contratos", icon: FileText, contractor: true },
  { to: "/ordenes-trabajo", label: "Órdenes de Trabajo", icon: ClipboardText },
  { to: "/estimaciones", label: "Estimaciones / Pagos", icon: Receipt, contractor: true },
  { to: "/estado-cuenta", label: "Estado de Cuenta", icon: Bank, contractor: true },
  { to: "/cobranza", label: "Erogaciones", icon: Coins },
  { to: "/acceso", label: "Control de Acceso", icon: Fingerprint, access: true },
  { to: "/partidas", label: "Avance / Calidad", icon: Stack, contractor: true },
  { to: "/bitacora", label: "Bitácora", icon: Notebook, contractor: true },
  { to: "/tareas", label: "Pendientes", icon: ListChecks },
  { to: "/programa", label: "Programa de Obra", icon: ChartBar },
  { to: "/chat", label: "Chat del equipo", icon: ChatCircleDots },
  { to: "/fotografias", label: "Fotos de obra", icon: Camera, contractor: true },
  { to: "/recorridos", label: "Recorridos 360°", icon: Compass },
  { to: "/empleados", label: "Empleados", icon: IdentificationCard },
  { to: "/documentos", label: "Planos y Estudios", icon: Blueprint },
  { to: "/bim", label: "BIM", icon: Cube },
  { to: "/normativa", label: "Reglamentos y Seguridad", icon: ShieldCheck },
  { to: "/legales", label: "Legal", icon: Scales },
  { to: "/contable", label: "Contable", icon: Coins },
  { to: "/concursos", label: "Concursos / Cotizaciones", icon: Scales },
  { to: "/reportes", label: "Reportes PDF", icon: FilePdf, contractor: true },
  { to: "/erp", label: "Reporte ERP (Pagos)", icon: Table, roles: ["admin", "gerente_general"] },
  { to: "/gastos-fijos", label: "Gastos Fijos", icon: Coins },
  { to: "/usuarios", label: "Usuarios", icon: UserGear, roles: ["admin"] },
  { to: "/correos", label: "Historial Correos", icon: Notebook, roles: ["admin"] },
  { to: "/auditoria", label: "Auditoría", icon: ClockCounterClockwise, roles: ["admin"] },
  { to: "/auditorias", label: "Auditorías externas", icon: ClipboardText },
  { to: "/eliminados", label: "Eliminados", icon: Trash },
  { to: "/portal-cliente", label: "Portal Cliente", icon: ShareNetwork, roles: ["admin"] },
  { to: "/whatsapp", label: "Alertas WhatsApp", icon: WhatsappLogo, roles: ["admin"] },
];

const TOUR_STEPS = [
  { to: null, title: "Bienvenido a Obra en Control", body: "Te doy un recorrido rápido por la aplicación. Usa \"Siguiente\" para avanzar. Puedes reabrir esta guía cuando quieras con el botón \"Guía\" arriba a la derecha." },
  { to: "/", title: "Panel", body: "Aquí ves las alertas clave: contratos por vencer, documentos faltantes, tareas vencidas y auditorías pendientes." },
  { to: "/contratistas", title: "Contratistas", body: "Da de alta a tus contratistas con su papelería fiscal (constancia, identificación, comprobante). El sistema avisa si falta algún documento." },
  { to: "/contratos", title: "Contratos con IA", body: "Sube el PDF del contrato y la IA resume fechas y penalizaciones, y ayuda a detectar posibles sobre-estimaciones." },
  { to: "/estimaciones", title: "Estimaciones y Pagos", body: "Registra estimaciones con su factura (obligatoria). Pasan por aprobaciones por niveles; los montos por encima del umbral los autoriza el Director General." },
  { to: "/estado-cuenta", title: "Estado de Cuenta", body: "Consulta el estado financiero global de toda la obra o por contratista: anticipos, retenciones y saldos." },
  { to: "/acceso", title: "Control de Acceso", body: "Registra entradas y salidas con foto. La IA compara el rostro contra la foto registrada del empleado y avisa si la identidad no coincide." },
  { to: "/empleados", title: "Empleados", body: "Alta de empleados con foto obligatoria; esa foto se usa para verificar su identidad en el control de acceso." },
  { to: "/concursos", title: "Concursos / Cotizaciones", body: "Compara cotizaciones con IA. Te alerta si no están cotizando lo mismo (volúmenes, alcance, tiempos o condiciones diferentes)." },
  { to: "/bitacora", title: "Bitácora", body: "Lleva la bitácora diaria de la obra, filtrable por contratista o global de la empresa." },
  { to: "/tareas", title: "Pendientes", body: "Crea y da seguimiento a tareas ligadas a contratos. Recibirás alertas de las que estén vencidas." },
  { to: "/fotografias", title: "Fotos de obra", body: "Sube fotografías por especialidad (obra civil, eléctrica, etc.) para documentar el avance." },
  { to: "/documentos", title: "Planos y Estudios", body: "Guarda planos y estudios técnicos; puedes subir carpetas completas de una sola vez." },
  { to: "/auditorias", title: "Auditorías externas", body: "Levanta auditorías con etiquetas y evidencias, y expórtalas a Excel." },
  { to: "/eliminados", title: "Eliminados", body: "Todo lo que se borra queda registrado aquí: quién, cuándo y qué. Puedes restaurarlo si fue un error." },
  { to: "/usuarios", title: "Usuarios y permisos", body: "Da de alta usuarios y define, por herramienta, si pueden Ver, Modificar o Eliminar. El administrador y el Director General ven y hacen todo." },
  { to: null, title: "¡Listo!", body: "Eso es lo esencial. Recuerda: los cambios se ven en el enlace de Preview; para tu dominio en vivo debes hacer Deploy. Reabre esta guía cuando quieras con el botón \"Guía\"." },
];

const isNavVisible = (n, user) => {
  if (n.external) return true;
  const role = user?.role;
  if (role === "admin" || role === "gerente_general") return true;
  if (role === "seguridad") return !!n.access;
  if (role === "contratista") return !!n.contractor;
  // Acceso total temporal: los demás roles internos ven toda la app excepto ERP y páginas exclusivas de admin
  if (n.roles) return false;
  if (n.to === "/erp") return false;
  return true;
};

export default function Layout({ children }) {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [faceOpen, setFaceOpen] = useState(false);
  const [faceBusy, setFaceBusy] = useState(false);
  const [faceImg, setFaceImg] = useState(null);
  const [tourOpen, setTourOpen] = useState(false);

  const enrollFace = async () => {
    if (!faceImg) return toast.error("Toma o sube una foto de tu rostro");
    setFaceBusy(true);
    try {
      const fd = new FormData(); fd.append("photo", faceImg);
      await api.post("/auth/face/enroll", fd);
      toast.success("Acceso facial activado");
      setUser({ ...user, face_enrolled: true });
      setFaceOpen(false); setFaceImg(null);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setFaceBusy(false); }
  };
  const disableFace = async () => {
    try { await api.delete("/auth/face/enroll"); toast.success("Acceso facial desactivado"); setUser({ ...user, face_enrolled: false }); setFaceOpen(false); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const tourSteps = TOUR_STEPS.filter((s) => !s.to || nav.some((n) => n.to === s.to && isNavVisible(n, user)));
  useEffect(() => {
    if (user && !localStorage.getItem("oc_tour_v1")) {
      localStorage.setItem("oc_tour_v1", "1");
      setTourOpen(true);
    }
  }, [user]);

  const handleLogout = async () => { await logout(); navigate("/login"); };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm) return toast.error("Las contraseñas no coinciden");
    if ((pwForm.new_password || "").length < 6) return toast.error("La nueva contraseña debe tener al menos 6 caracteres");
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pwForm.current_password, new_password: pwForm.new_password });
      toast.success("Contraseña actualizada");
      setPwOpen(false); setPwForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setPwBusy(false); }
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 px-6 h-16 border-b border-border">
        <div className="w-9 h-9 bg-primary rounded-sm flex items-center justify-center">
          <HardHat size={22} weight="bold" className="text-white" />
        </div>
        <div className="leading-tight">
          <div className="font-heading font-extrabold tracking-tight text-lg">Obra en Control</div>
          <div className="overline !text-[9px]">Gestión de contratistas</div>
        </div>
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {nav.filter((n) => isNavVisible(n, user)).map((n) => (
          n.external ? (
            <a
              key={n.href}
              href={n.href}
              target="_blank"
              rel="noreferrer"
              onClick={() => setOpen(false)}
              data-testid={`nav-ext-${n.label.toLowerCase().split(" ")[0]}`}
              className="flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-bold tracking-wide transition-colors duration-200 text-primary hover:bg-primary/10"
            >
              <n.icon size={19} weight="bold" />
              {n.label}
              <ArrowSquareOut size={14} weight="bold" className="ml-auto opacity-70" />
            </a>
          ) : (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.exact}
            onClick={() => setOpen(false)}
            data-testid={`nav-${n.to === "/" ? "panel" : n.to.slice(1)}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors duration-200 ${
                isActive ? "bg-primary text-white" : "text-muted-foreground hover:bg-muted hover:text-foreground"
              }`
            }
          >
            <n.icon size={19} weight="bold" />
            {n.label}
          </NavLink>
          )
        ))}
      </nav>
      <div className="p-3 border-t border-border">
        <div className="px-3 py-2">
          <div className="text-sm font-semibold truncate">{user?.name}</div>
          <div className="text-xs text-muted-foreground capitalize">{user?.role}</div>
        </div>
        <button
          onClick={() => { setPwForm({ current_password: "", new_password: "", confirm: "" }); setPwOpen(true); }}
          data-testid="change-pw-btn"
          className="w-full flex items-center gap-2 px-3 py-2.5 rounded-sm text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors duration-200"
        >
          <Key size={19} weight="bold" /> Cambiar contraseña
        </button>
        <button
          onClick={() => { setFaceImg(null); setFaceOpen(true); }}
          data-testid="face-enroll-btn"
          className="w-full flex items-center gap-2 px-3 py-2.5 rounded-sm text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors duration-200"
        >
          <Fingerprint size={19} weight="bold" /> {user?.face_enrolled ? "Acceso facial activado" : "Activar acceso facial"}
        </button>
        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          className="w-full flex items-center gap-2 px-3 py-2.5 rounded-sm text-sm font-medium text-danger hover:bg-red-50 transition-colors duration-200"
        >
          <SignOut size={19} weight="bold" /> Cerrar sesión
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-secondary">
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-64 bg-card border-r border-border z-30">
        <SidebarContent />
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="relative w-64 bg-card border-r border-border"><SidebarContent /></aside>
        </div>
      )}

      <div className="lg:pl-64">
        <header className="h-16 bg-card border-b border-border flex items-center px-4 lg:px-8 sticky top-0 z-20">
          <button className="lg:hidden mr-3" onClick={() => setOpen(true)} data-testid="menu-toggle">
            {open ? <X size={24} /> : <List size={24} />}
          </button>
          <div className="font-heading font-bold tracking-tight hidden md:block whitespace-nowrap mr-4">Plaza Tarantella · Obra</div>
          <div className="flex-1 flex justify-center px-2"><GlobalSearch /></div>
          <NotificationsBell />
          <button onClick={() => setTourOpen(true)} data-testid="tour-btn" className="ml-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary border border-primary/30 rounded-sm px-3 py-1.5 hover:bg-primary/5 transition-colors duration-200 shrink-0">
            <MapTrifold size={16} weight="bold" /> Guía
          </button>
        </header>
        <main className="p-4 lg:p-8 max-w-[1400px]">{children}</main>
      </div>

      <GuidedTour open={tourOpen} onClose={() => setTourOpen(false)} steps={tourSteps} navigate={navigate} />

      <Modal open={pwOpen} onClose={() => setPwOpen(false)} title="Cambiar mi contraseña">
        <form onSubmit={changePassword} className="space-y-4">
          <Field label="Contraseña actual *"><input data-testid="pw-current" type="password" required className={inputClass} value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} /></Field>
          <Field label="Nueva contraseña *"><input data-testid="pw-new" type="password" required minLength={6} className={inputClass} value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} /></Field>
          <Field label="Confirmar nueva contraseña *"><input data-testid="pw-confirm" type="password" required className={inputClass} value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} /></Field>
          <PrimaryButton type="submit" disabled={pwBusy} data-testid="pw-save-btn">{pwBusy ? "Guardando…" : "Actualizar contraseña"}</PrimaryButton>
        </form>
      </Modal>

      <Modal open={faceOpen} onClose={() => setFaceOpen(false)} title="Acceso facial">
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">Registra tu rostro para poder entrar con reconocimiento facial, además de tu contraseña. Toma una foto clara de tu cara con buena luz.</p>
          <CameraCapture onCapture={setFaceImg} />
          <PrimaryButton onClick={enrollFace} disabled={faceBusy} data-testid="face-enroll-save-btn">{faceBusy ? "Guardando…" : (user?.face_enrolled ? "Actualizar mi rostro" : "Activar acceso facial")}</PrimaryButton>
          {user?.face_enrolled && <button type="button" onClick={disableFace} data-testid="face-disable-btn" className="w-full text-sm text-danger hover:underline">Desactivar acceso facial</button>}
        </div>
      </Modal>

      <ChatWidget />
    </div>
  );
}
